import { ButtonContent } from '../ui/ButtonContent';
import { InsightText } from './report/InsightText';
import type { ReportAiContext } from '../../functions/api/gemini/content-pipeline/types';
/**
 * VideoTranscriptViewer Component
 *
 * Integrated video player with synchronized transcript:
 * - Main video player at top (mobile) or left (desktop)
 * - Transcript synced with video playback
 * - Click transcript to seek to that point in video
 * - Multi-segment support with thumbnails
 */

import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { captureException } from '../../lib/telemetry/posthog/frontend';
import { highlightTurn } from '../../lib/utils/transcript/highlight-turn';
import Spinner from '../Spinner';
import { DownloadIcon } from '../icons/DownloadIcon';
import { IconClipboard, IconClipboardCheck, IconDownload } from '@tabler/icons-react';
import type { VideoSegment, ConversationTurn, TranscriptTiming } from '../../types';
import { useSegmentPlayback, buildSegmentDownloadFilename } from '../hooks/useSegmentPlayback';
import {
  buildTimeline,
  getTurnTiming,
  locate,
  seekTargetForTurn,
  seekTargetForWord,
  segmentKey,
  turnKeyForTurn,
  visiblePlaybackSegments,
  type SeekTarget,
} from '../../lib/transcript-timing';

// Re-exported so existing `from '../VideoTranscriptViewer'` test imports keep
// working after the filename logic moved into useSegmentPlayback.
export { buildSegmentDownloadFilename };

/**
 * Window (ms) during which a scroll event is treated as the viewer's own
 * programmatic `scrollIntoView` rather than a user gesture. Browser scrolling
 * can emit several events; this covers them so follow mode does not mistake
 * its own movement for the user taking over.
 */
const PROGRAMMATIC_SCROLL_WINDOW_MS = 700;

const USER_SCROLL_KEYS = new Set([
  'ArrowDown',
  'ArrowUp',
  'End',
  'Home',
  'PageDown',
  'PageUp',
  ' ',
]);

/**
 * Workaround for the chunked-WebM `duration === Infinity` bug: MediaRecorder
 * written with a timeslice produces WebM blobs with no Cues / duration in the
 * EBML header, so `<audio>.duration` reads as `Infinity` and the scrubber
 * compresses real playback into the last sliver of the bar.
 *
 * Trick: seek to a huge timestamp, which forces the browser to scan the
 * stream and compute the real duration, then seek back to 0. After that
 * `duration` becomes a real number and the scrubber tracks playback correctly.
 *
 * Returns a teardown function so the caller can detach the listener if the
 * element unmounts before metadata loads.
 */
function attachAudioDurationFix(audio: HTMLMediaElement): () => void {
  const HUGE_TIME = 1e101;

  const onDurationKnown = () => {
    if (Number.isFinite(audio.duration) && audio.duration > 0) {
      audio.removeEventListener('durationchange', onDurationKnown);
      // Seek back to 0; browser may need a tick to settle after the huge seek.
      try { audio.currentTime = 0; } catch { /* ignore */ }
    }
  };

  const onLoadedMetadata = () => {
    if (!Number.isFinite(audio.duration)) {
      audio.addEventListener('durationchange', onDurationKnown);
      try { audio.currentTime = HUGE_TIME; } catch { /* ignore */ }
    }
  };

  audio.addEventListener('loadedmetadata', onLoadedMetadata);
  return () => {
    audio.removeEventListener('loadedmetadata', onLoadedMetadata);
    audio.removeEventListener('durationchange', onDurationKnown);
  };
}

interface VideoTranscriptViewerProps {
  /** Sourced context, indexed by the original one-based guest turn. */
  aiContext?: ReportAiContext[];
  sessionId: string;
  /**
   * The interview_data row ID for the response being viewed. Required when the
   * session has multiple responses - without it the video/thumbnail endpoints
   * fall back to the latest response_number and end up streaming the wrong
   * recording (see functions/api/videos/[sessionId]/[segmentIndex].ts).
   */
  responseId?: string;
  videoEnabled: boolean;
  /** Text participation does not imply that a recording was attempted. */
  recordingOptional?: boolean;
  videoSegments?: VideoSegment[] | null;
  conversationTurns: ConversationTurn[];
  /**
   * Persisted `transcript_timings` rows for this response (Stage 2 forced
   * alignment). Ground truth when present; an empty array (the Stage 1 default)
   * drives the utterance-level wall-clock fallback with no user-facing error.
   */
  timingRows?: TranscriptTiming[];
  /**
   * Unix timestamp of session.created_at. Retained for API compatibility; the
   * timeline now anchors on each segment's own recorder clock, so the viewer no
   * longer derives transcript time from the session start.
   */
  sessionStartTime: number;
  // Translation & export props
  originalLanguage?: string;
  translatedTurns?: ConversationTurn[] | null;
  translatedLanguage?: string | null;
  showTranslated?: boolean;
  onToggleTranslated?: (show: boolean) => void;
  canTranslate?: boolean;
  onTranslate?: () => void;
  translating?: boolean;
  translationError?: string | null;
  onCopyTranscript?: () => void;
  onDownloadTranscript?: () => void;
  copiedTranscript?: boolean;
  scrollToTurnIndex?: number | null;
}

export const VideoTranscriptViewer: React.FC<VideoTranscriptViewerProps> = ({
  sessionId,
  responseId,
  aiContext,
  videoEnabled,
  recordingOptional = false,
  videoSegments,
  conversationTurns,
  timingRows,
  // Translation & export props
  originalLanguage,
  translatedTurns,
  translatedLanguage,
  showTranslated = false,
  onToggleTranslated,
  canTranslate = false,
  onTranslate,
  translating = false,
  translationError,
  onCopyTranscript,
  onDownloadTranscript,
  copiedTranscript = false,
  scrollToTurnIndex,
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const transcriptRef = useRef<HTMLDivElement>(null);
  const turnRefs = useRef<Map<number, HTMLDivElement>>(new Map());

  const [activeSegmentIndex, setActiveSegmentIndex] = useState(0);
  // Playhead offset into the ACTIVE segment's media, in ms (video.currentTime *
  // 1000). This is the coordinate the timeline utility's locate() expects.
  const [currentSegmentOffsetMs, setCurrentSegmentOffsetMs] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  // Whether the playhead-follow scroll is currently active. It stays suspended
  // after the user scrolls so their explicit reading position is respected.
  const [autoScroll, setAutoScroll] = useState(true);
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);
  // Thumbnail URLs that failed to load, keyed by the exact (token-bearing) URL.
  // Keying by URL means a fresh signed token (re-minted every few minutes)
  // produces a new key and retries, so a transient failure self-heals instead of
  // permanently hiding the thumbnail (A21).
  const [failedThumbs, setFailedThumbs] = useState<Set<string>>(new Set());
  // In-memory object URL for the active audio segment. Audio is chunked WebM with
  // no seek cues, so it is only scrubbable when the whole file is buffered in
  // memory rather than streamed via a `<source>` URL. Null until the blob lands.
  const [audioObjectUrl, setAudioObjectUrl] = useState<string | null>(null);

  // Playback-visibility rule: hide an orphaned audio sidecar from every playback
  // surface (player, thumbnails, strip, download) whenever a processed camera
  // segment exists, while an audio-only call keeps its audio segments. Filter by
  // KEY so the survivors stay fully-typed VideoSegments for useSegmentPlayback.
  const visibleSegments = useMemo(() => {
    const raw = videoSegments || [];
    const visibleKeys = new Set(visiblePlaybackSegments(raw).map(segmentKey));
    return raw.filter((s) => visibleKeys.has(segmentKey(s)));
  }, [videoSegments]);

  // Segment playback data/URL layer (partitioning, response-scoped URL
  // builders, authenticated download) lives in useSegmentPlayback so this
  // component keeps only the playback-control + transcript sync. It is fed the
  // visibility-filtered set so orphaned audio never reaches the player surfaces.
  const {
    processedSegments,
    processingSegments,
    failedSegments,
    hasProcessedVideo,
    hasFailedSegments,
    allFailed,
    segmentSrc,
    thumbnailSrc,
    hasPlaybackToken,
    fetchSegmentObjectUrl,
    download: handleDownload,
    downloading,
    error,
  } = useSegmentPlayback({ sessionId, responseId, segments: visibleSegments });

  // The single source of transcript<->media mapping. Rebuilt only when the
  // segments, timing rows, or turns change. Fed the processed (already
  // visibility-filtered) segments so its lookups match the playable surfaces.
  const timeline = useMemo(
    () => buildTimeline(processedSegments, timingRows || [], conversationTurns),
    [processedSegments, timingRows, conversationTurns],
  );

  const activeSegment = processedSegments[activeSegmentIndex];
  const isPortrait = activeSegment?.dimensions
    ? activeSegment.dimensions.height > activeSegment.dimensions.width
    : false;
  // Camera was likely denied if video was enabled but no segments exist at all (not even uploading/processing)
  const cameraLikelyDenied = !recordingOptional && videoEnabled && (!videoSegments || videoSegments.length === 0);

  // Calculate video time (relative to first segment start) from a segment index
  // and in-segment offset. Used only for the read-only time labels.
  const getVideoTimeMs = useCallback((segmentIndex: number, segmentPositionMs: number) => {
    if (!processedSegments.length) return 0;
    const segment = processedSegments[segmentIndex];
    if (!segment) return 0;
    // Time relative to video start = (this segment's absolute start - first segment's absolute start) + position in segment
    const firstSegmentStart = processedSegments[0].start_ms;
    return (segment.start_ms - firstSegmentStart) + segmentPositionMs;
  }, [processedSegments]);

  // Index once because every visible turn resolves its segment during render.
  const processedSegmentIndexByKey = useMemo(() => {
    const indexes = new Map<string, number>();
    processedSegments.forEach((segment, index) => {
      indexes.set(segmentKey(segment), index);
    });
    return indexes;
  }, [processedSegments]);

  // The visible timestamp describes the same position as clicking the turn.
  // Non-seekable rows retain their legacy wall-clock marker when available.
  const getTurnDisplayTimeMs = useCallback((
    turn: ConversationTurn,
    target: SeekTarget | null,
  ): number | null => {
    if (target) {
      const segmentIndex = processedSegmentIndexByKey.get(target.segmentKey);
      if (segmentIndex !== undefined) {
        return getVideoTimeMs(segmentIndex, target.offsetMs);
      }
    }
    if (!turn.timestamp || !processedSegments.length) return null;
    return turn.timestamp - processedSegments[0].start_ms;
  }, [getVideoTimeMs, processedSegmentIndexByKey, processedSegments]);

  // The turn (and word) active at the current playhead, resolved by the timeline
  // utility from the active segment's key + in-segment offset. Holds the nearest
  // preceding turn steady through pauses, for guest and host alike.
  const active = useMemo(() => {
    if (!activeSegment) return null;
    return locate(timeline, segmentKey(activeSegment), currentSegmentOffsetMs);
  }, [timeline, activeSegment, currentSegmentOffsetMs]);
  const activeTurnKey = active?.turnKey ?? null;
  const activeWordIndex = active?.wordIndex ?? null;

  // Video-relative time of the playhead, for the header time label.
  const currentVideoTimeMs = getVideoTimeMs(activeSegmentIndex, currentSegmentOffsetMs);

  // Format time display
  const formatTime = (ms: number) => {
    if (ms < 0) ms = 0;
    const totalSeconds = Math.floor(ms / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  // Update current video time from video element events
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return undefined;

    const handleTimeUpdate = () => {
      setCurrentSegmentOffsetMs(video.currentTime * 1000);
    };

    const handlePlay = () => setIsPlaying(true);
    const handlePause = () => setIsPlaying(false);

    const handleEnded = () => {
      // Auto-advance to next segment
      if (activeSegmentIndex < processedSegments.length - 1) {
        setActiveSegmentIndex(prev => prev + 1);
      } else {
        setIsPlaying(false);
      }
    };

    video.addEventListener('timeupdate', handleTimeUpdate);
    video.addEventListener('play', handlePlay);
    video.addEventListener('pause', handlePause);
    video.addEventListener('ended', handleEnded);

    // Audio segments come from MediaRecorder chunked WebM which omits the
    // duration in its EBML header - the seek-to-huge dance forces the browser
    // to compute the real duration so the scrubber doesn't bunch playback
    // into the last sliver of the bar. See `attachAudioDurationFix`.
    const detachDurationFix =
      activeSegment?.segment_kind === 'audio' ? attachAudioDurationFix(video) : null;

    return () => {
      video.removeEventListener('timeupdate', handleTimeUpdate);
      video.removeEventListener('play', handlePlay);
      video.removeEventListener('pause', handlePause);
      video.removeEventListener('ended', handleEnded);
      detachDurationFix?.();
    };
    // audioObjectUrl: the audio element only mounts once its blob lands, so the
    // effect must re-run then to bind the time-sync listeners to that element.
  }, [activeSegmentIndex, processedSegments.length, activeSegment?.segment_kind, audioObjectUrl]);

  // Auto-play when segment changes (if was playing)
  useEffect(() => {
    const video = videoRef.current;
    if (video && isPlaying) {
      video.play().catch((err) => {
        // AbortError/NotAllowedError are expected browser autoplay policy - skip
        if (err instanceof Error && (err.name === 'AbortError' || err.name === 'NotAllowedError')) return;
        captureException(err instanceof Error ? err : String(err), 'VideoTranscriptViewer.autoPlay', { sessionId, activeSegmentIndex });
      });
    }
  }, [activeSegmentIndex, isPlaying, sessionId]);

  // Load the active audio segment fully into memory and play it from an object
  // URL. Chunked WebM (browser MediaRecorder, 2s timeslice, stored raw) has no
  // finalized EBML header, so a streamed `<source>` URL is not seekable; with the
  // whole file buffered the browser builds its own seek index and the scrubber
  // works, with no server-side remux. Video/screen segments stream as before.
  //
  // TODO(audio-pipeline): this in-memory approach is bounded by browser RAM. It
  // is comfortable up to ~100MB (roughly 1-2hr of Opus audio at ~0.4-1MB/min) and
  // risky above a few hundred MB on mobile. When we support long-form recordings,
  // adapt audio to the same processing pipeline as video instead of buffering the
  // whole file client-side:
  //   1. Route audio segments through a worker (parallel to the video-processor
  //      that excludes audio today, see workers/live-session/src/video-handler.ts
  //      and workers/video-processor) that remuxes the raw WebM to a finalized
  //      container with a real Segment duration + Cues/SeekHead (ffmpeg
  //      `-c copy` is enough, no re-encode), marking it status='processed'.
  //   2. Once segments carry a seekable header, drop this blob fetch and revert
  //      audio to the streamed token `<source>` URL (segmentSrc) used by video,
  //      which seeks via the endpoint's existing HTTP Range support. The
  //      attachAudioDurationFix hack can then be removed too.
  //   3. Interim option if needed before the pipeline lands: guard on
  //      activeSegment.size_bytes and fall back to the streamed source above a
  //      threshold (loses scrubbing, but avoids OOM on very large files).
  useEffect(() => {
    if (activeSegment?.segment_kind !== 'audio') {
      setAudioObjectUrl(null);
      return undefined;
    }

    let cancelled = false;
    let createdUrl: string | null = null;

    fetchSegmentObjectUrl(activeSegment)
      .then((url) => {
        if (!url) return;
        if (cancelled) {
          URL.revokeObjectURL(url);
          return;
        }
        createdUrl = url;
        setAudioObjectUrl(url);
      })
      .catch((err) => {
        captureException(
          err instanceof Error ? err : String(err),
          'VideoTranscriptViewer.loadAudioBlob',
          { sessionId, segmentIndex: activeSegment.segment_index },
        );
      });

    return () => {
      cancelled = true;
      if (createdUrl) URL.revokeObjectURL(createdUrl);
    };
  }, [activeSegment, fetchSegmentObjectUrl, sessionId]);

  // The turns actually on screen (original or translated), shared by the
  // follow-scroll effect and the render so their indexes line up.
  const isTranslatedView = showTranslated && Boolean(translatedTurns);
  const displayTurns = isTranslatedView && translatedTurns ? translatedTurns : conversationTurns;

  // Timeline keys for the displayed rows, resolved POSITIONALLY through the
  // original turns. Translated turns come back from an LLM that preserves
  // timestamps but not necessarily turn_id, so keying a translated row by its
  // own fields can miss the timeline (built from the originals) entirely. The
  // translation contract keeps the arrays index-aligned, so the original at
  // the same index is the row's identity; a translated row with no original
  // counterpart falls back to its own key and simply resolves to nothing.
  const displayTurnKeys = useMemo(
    () => displayTurns.map((t, i) => turnKeyForTurn(
      displayTurns === conversationTurns ? t : conversationTurns[i] ?? t,
    )),
    [displayTurns, conversationTurns],
  );

  // Index of the active turn within the displayed turns, for the follow scroll.
  const activeTurnIndex = useMemo(() => {
    if (!activeTurnKey) return -1;
    return displayTurnKeys.indexOf(activeTurnKey);
  }, [displayTurnKeys, activeTurnKey]);

  // Marks the window during which a scroll event is our own smooth scroll, so
  // the follow does not misread its own motion as the user taking over.
  const programmaticScrollUntilRef = useRef(0);

  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return undefined;
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    const updatePreference = () => setPrefersReducedMotion(mediaQuery.matches);
    updatePreference();
    mediaQuery.addEventListener('change', updatePreference);
    return () => mediaQuery.removeEventListener('change', updatePreference);
  }, []);

  // Suspend the follow scroll the moment the user scrolls the transcript
  // themselves. The preference lasts for this viewer instance so playback does
  // not repeatedly take control away from someone reading another turn.
  useEffect(() => {
    const container = transcriptRef.current;
    if (!container) return undefined;

    const handleScroll = () => {
      // Ignore the scroll events our own scrollIntoView emits.
      if (Date.now() < programmaticScrollUntilRef.current) return;
      setAutoScroll(false);
    };
    const handleUserScrollIntent = () => setAutoScroll(false);
    const handleKeyDown = (event: KeyboardEvent) => {
      if (USER_SCROLL_KEYS.has(event.key)) setAutoScroll(false);
    };

    container.addEventListener('scroll', handleScroll, { passive: true });
    container.addEventListener('wheel', handleUserScrollIntent, { passive: true });
    container.addEventListener('touchstart', handleUserScrollIntent, { passive: true });
    container.addEventListener('pointerdown', handleUserScrollIntent, { passive: true });
    container.addEventListener('keydown', handleKeyDown);
    return () => {
      container.removeEventListener('scroll', handleScroll);
      container.removeEventListener('wheel', handleUserScrollIntent);
      container.removeEventListener('touchstart', handleUserScrollIntent);
      container.removeEventListener('pointerdown', handleUserScrollIntent);
      container.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  // Keep the active turn comfortably in view while the follow is active.
  useEffect(() => {
    if (!autoScroll || activeTurnIndex < 0) return;
    const turnEl = turnRefs.current.get(activeTurnIndex);
    const container = transcriptRef.current;
    if (!turnEl || !container) return;

    const containerRect = container.getBoundingClientRect();
    const turnRect = turnEl.getBoundingClientRect();
    // Only scroll when the active row has drifted out of the visible area.
    if (turnRect.top < containerRect.top || turnRect.bottom > containerRect.bottom) {
      programmaticScrollUntilRef.current = Date.now() + PROGRAMMATIC_SCROLL_WINDOW_MS;
      turnEl.scrollIntoView({
        behavior: prefersReducedMotion ? 'instant' : 'smooth',
        block: 'center',
      });
    }
  }, [activeTurnIndex, autoScroll, prefersReducedMotion]);

  // Scroll to a specific turn when navigating from the report
  useEffect(() => {
    if (scrollToTurnIndex == null) return undefined;

    const turnEl = turnRefs.current.get(scrollToTurnIndex);
    if (turnEl) {
      return highlightTurn(turnEl);
    }
    return undefined;
  }, [scrollToTurnIndex]);

  // Seek the player to a resolved timeline target: select its home segment (by
  // key, since post-filter array indexes are not stable) and set the playhead to
  // the target offset once the element is playable.
  const seekToTarget = useCallback((target: SeekTarget) => {
    const segmentIndex = processedSegments.findIndex((s) => segmentKey(s) === target.segmentKey);
    if (segmentIndex < 0) return;

    const applySeek = () => {
      if (!videoRef.current) return;
      videoRef.current.currentTime = target.offsetMs / 1000;
      videoRef.current.play().catch((err) => {
        if (err instanceof Error && (err.name === 'AbortError' || err.name === 'NotAllowedError')) return;
        captureException(err instanceof Error ? err : String(err), 'VideoTranscriptViewer.seekPlay', { sessionId, segmentIndex });
      });
    };

    if (segmentIndex !== activeSegmentIndex) {
      // Switch segments first; the element remounts, so apply the seek after it.
      setActiveSegmentIndex(segmentIndex);
      setTimeout(applySeek, 150);
    } else {
      applySeek();
    }

    // Browsing disables follow mode so the clicked target stays put.
    setAutoScroll(false);
  }, [processedSegments, activeSegmentIndex, sessionId]);

  // Handle clicking on a transcript turn: seek at utterance accuracy via the
  // timeline utility. Non-seekable turns (system, off-recording) resolve to
  // null. Takes the row's resolved timeline key (positional through the
  // originals) so translated rows seek too.
  const handleTurnClick = useCallback((turnKey: string) => {
    if (!hasProcessedVideo) return;
    const target = seekTargetForTurn(timeline, turnKey);
    if (target) seekToTarget(target);
  }, [hasProcessedVideo, timeline, seekToTarget]);

  // Handle clicking a word span (Stage 2): seek at word accuracy. Words in the
  // unspoken interruption tail resolve to null and never fire this.
  const handleWordClick = useCallback((turnKey: string, wordIndex: number) => {
    if (!hasProcessedVideo) return;
    const target = seekTargetForWord(timeline, turnKey, wordIndex);
    if (target) seekToTarget(target);
  }, [hasProcessedVideo, timeline, seekToTarget]);

  // Handle segment thumbnail click
  const handleSegmentClick = (index: number) => {
    setActiveSegmentIndex(index);
    if (videoRef.current) {
      videoRef.current.currentTime = 0;
    }
  };

  // Audio-only calls also flow through this viewer. We render an <audio>
  // element instead of <video> when the active segment is audio-kind. The
  // `videoEnabled=false` early-return that used to live here is gone - the
  // viewer is now the single playback surface for both call types. Based on the
  // visibility-filtered set so an orphaned audio sidecar cannot keep the player
  // column alive on a video call.
  const hasVideoSegments = visibleSegments.length > 0;
  const isAudioOnly = activeSegment?.segment_kind === 'audio';

  if (!hasVideoSegments && conversationTurns.length === 0) {
    return (
      <div className="bg-surface/50 rounded-lg p-4 mb-4">
        <p className="text-muted-foreground text-center text-sm">
          {recordingOptional ? 'No transcript available yet.' : 'No video or transcript available yet.'}
        </p>
      </div>
    );
  }

  return (
    <div className={`grid grid-cols-1 ${hasVideoSegments ? 'lg:grid-cols-2' : ''} gap-4`}>
      {/* Video Player Section - Left on desktop (only shown when video exists) */}
      {hasVideoSegments && <div className="bg-background/50 border border-border/50 rounded-2xl overflow-hidden h-fit">
        {/* Player - <audio> for audio-only segments, <video> otherwise. Both
            share the videoRef so the existing time-update + seek logic keeps
            working (HTMLMediaElement covers both types). */}
        {hasProcessedVideo && activeSegment ? (
          <div className="relative">
            {isAudioOnly ? (
              // Audio-only card: native <audio controls> is a fixed ~40px strip
              // and looks lost in a wide grid cell, so we wrap it in a dark
              // card with a mic glyph to give it visual weight comparable to
              // the video player's aspect-video tile.
              <div className="bg-background px-6 py-10 flex flex-col items-center gap-6">
                <div className="w-20 h-20 rounded-full bg-wash-accent flex items-center justify-center">
                  <svg className="w-10 h-10 text-volt-soft" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.6}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 18.75a6 6 0 006-6v-1.5m-6 7.5a6 6 0 01-6-6v-1.5m6 7.5v3.75m-3.75 0h7.5M12 15.75a3 3 0 01-3-3V4.5a3 3 0 116 0v8.25a3 3 0 01-3 3z" />
                  </svg>
                </div>
                {audioObjectUrl ? (
                  <audio
                    ref={videoRef as React.RefObject<HTMLAudioElement>}
                    className="w-full max-w-md"
                    controls
                    // The source is an in-memory blob of the whole file, so the
                    // browser can compute the duration and seek cues that the raw
                    // chunked WebM lacks. Key on the URL so the element reloads
                    // when the blob lands (a media element does not reload on a
                    // bare <source src> change).
                    key={`${sessionId}-audio-${activeSegment.segment_index}-${audioObjectUrl}`}
                  >
                    <source
                      src={audioObjectUrl}
                      type={activeSegment.mime_type || 'audio/webm'}
                    />
                  </audio>
                ) : (
                  // Blob still downloading: motion carries the meaning, no caption.
                  <div className="w-full max-w-md flex justify-center py-2">
                    <Spinner size="w-6 h-6" />
                  </div>
                )}
              </div>
            ) : (
              <video
                ref={videoRef}
                className={`w-full bg-background ${isPortrait ? 'aspect-[9/16] max-h-[70vh] mx-auto' : 'aspect-video'}`}
                controls
                preload="metadata"
                // Key on kind+index so switching between same-index camera/screen
                // segments remounts the player and picks up the new <source> URL.
                // The token-readiness suffix also remounts it when the async
                // signed token lands (a <video> does not reload on a bare
                // <source src> change, so it would otherwise stay stuck on the
                // dead empty-token source it first painted).
                key={`${sessionId}-${activeSegment.segment_kind || 'camera'}-${activeSegment.segment_index}-${hasPlaybackToken(activeSegment) ? 'tok' : 'wait'}`}
              >
                <source
                  src={segmentSrc(activeSegment)}
                  type="video/mp4"
                />
              </video>
            )}
            {processedSegments.length > 1 && (
              <div className="absolute top-3 right-3 px-2 py-1 bg-background/80 rounded-lg text-xs text-foreground">
                {activeSegment.segment_kind === 'screen'
                  ? 'Screen share'
                  : activeSegment.segment_kind === 'audio'
                  ? 'Audio'
                  : `Segment ${activeSegment.segment_index + 1}`}
              </div>
            )}
          </div>
        ) : processingSegments.length > 0 ? (
          <div className={`${isPortrait ? 'aspect-[9/16] max-h-[70vh] mx-auto' : 'aspect-video'} bg-background flex flex-col items-center justify-center`}>
            <Spinner size="w-12 h-12" />
            <p className="mt-4 text-foreground font-medium">Processing Video</p>
            <p className="mt-2 text-sm text-muted-foreground">
              {processingSegments.length} segment{processingSegments.length > 1 ? 's' : ''} processing...
            </p>
          </div>
        ) : allFailed ? (
          // Terminal failure: every segment failed. Minimal, spinner-less, a
          // broken-frame glyph carries the meaning so no long caption is needed.
          <div className={`${isPortrait ? 'aspect-[9/16] max-h-[70vh] mx-auto' : 'aspect-video'} bg-background flex flex-col items-center justify-center gap-3`}>
            <svg className="w-12 h-12 text-danger/70" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.243 4.243L9.88 9.88" />
            </svg>
            <p className="text-muted-foreground">video unavailable</p>
            <p className="text-sm text-muted-foreground">
              {failedSegments.length} segment{failedSegments.length > 1 ? 's' : ''} failed
            </p>
          </div>
        ) : (
          <div className={`${isPortrait ? 'aspect-[9/16] max-h-[70vh] mx-auto' : 'aspect-video'} bg-background flex items-center justify-center`}>
            <p className="text-muted-foreground">No processed video available</p>
          </div>
        )}

        {/* Segment Thumbnails */}
        {processedSegments.length > 1 && (
          <div className="p-3 border-t border-border bg-background/50">
            <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-dark">
              {processedSegments.map((segment, idx) => (
                <button
                  key={`${segment.segment_kind || 'camera'}-${segment.segment_index}`}
                  onClick={() => handleSegmentClick(idx)}
                  className={`relative flex-shrink-0 w-24 rounded-lg overflow-hidden border-2 transition-all ${
                    idx === activeSegmentIndex
                      ? 'border-volt ring-2 ring-volt/30'
                      : 'border-border hover:border-border'
                  }`}
                >
                  <div className="aspect-video bg-surface flex items-center justify-center">
                    {segment.thumbnail_path && thumbnailSrc(segment) && !failedThumbs.has(thumbnailSrc(segment)) ? (
                      <img
                        src={thumbnailSrc(segment)}
                        alt={`${segment.segment_kind === 'screen' ? 'Screen' : 'Segment'} ${segment.segment_index + 1} thumbnail`}
                        className="w-full h-full object-cover"
                        loading="lazy"
                        // React-state fallback (not imperative DOM mutation): mark
                        // this exact URL failed so we render the play icon. A later
                        // token re-mint yields a new URL that retries (A21).
                        onError={() => {
                          const failedUrl = thumbnailSrc(segment);
                          setFailedThumbs((prev) => {
                            const next = new Set(prev);
                            next.add(failedUrl);
                            return next;
                          });
                        }}
                      />
                    ) : (
                      <svg className="w-5 h-5 text-muted-foreground" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M8 5v14l11-7z" />
                      </svg>
                    )}
                  </div>
                  <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-background/90 to-transparent p-1">
                    <p className="text-xs text-foreground font-medium">
                      {/* Number by the segment's own recording counter (per-kind),
                          not the array position - avoids labelling the first camera
                          recording as "Seg 2" when a screen share sorts ahead of it. */}
                      {segment.segment_kind === 'screen' ? 'Screen' : `Seg ${segment.segment_index + 1}`}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {formatTime(segment.end_ms - segment.start_ms)}
                    </p>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Download */}
        {hasProcessedVideo && (
          <div className="p-3 border-t border-border flex items-center justify-between bg-background/50">
            <span className="text-sm text-muted-foreground flex items-center gap-2">
              {processedSegments.length} segment{processedSegments.length > 1 ? 's' : ''} •{' '}
              {formatFileSize(processedSegments.reduce((acc, s) => acc + s.size_bytes, 0))}
              {hasFailedSegments && (
                <span className="px-1.5 py-0.5 rounded-lg text-xs bg-danger/15 text-danger">
                  {failedSegments.length} failed
                </span>
              )}
            </span>
            <button aria-busy={downloading !== null}
              onClick={() => handleDownload(activeSegment)}
              disabled={downloading !== null}
              className="flex items-center gap-2 px-3 py-1.5 bg-surface-raised hover:bg-surface-hover text-foreground text-sm rounded-lg transition-colors disabled:opacity-50"
            >
              <ButtonContent loading={downloading !== null}><DownloadIcon className="w-4 h-4" />Download</ButtonContent>
            </button>
          </div>
        )}

        {/* Processing indicator */}
        {processingSegments.length > 0 && (
          <div className="p-3 border-t border-border bg-surface/30">
            <div className="flex items-center gap-2 text-sm">
              <Spinner size="w-4 h-4" />
              <span className="text-muted-foreground">
                {processingSegments.length} segment{processingSegments.length > 1 ? 's' : ''} processing...
              </span>
            </div>
          </div>
        )}

        {error && (
          <div className="p-3 border-t border-border bg-danger/10 text-danger text-sm">
            {error}
          </div>
        )}
      </div>}

      {/* Transcript Section - Right on desktop, full width when no video */}
      <div className="bg-background/50 border border-border/50 rounded-2xl overflow-hidden flex flex-col">
        {/* Transcript Header */}
        <div className="p-4 border-b border-border flex-shrink-0 space-y-3">
          {/* Title row */}
          <div className="flex items-center justify-between">
            <h3 className="font-bold text-foreground">Transcript</h3>
            {hasProcessedVideo && (
              <span className="text-xs text-muted-foreground">
                {formatTime(currentVideoTimeMs)}
              </span>
            )}
          </div>

          {/* Camera denied warning */}
          {cameraLikelyDenied && conversationTurns.length > 0 && (
            <div className="flex items-start gap-2 p-3 bg-wash-warning rounded-lg">
              <svg className="w-4 h-4 text-warning-soft flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
              <p className="text-sm text-foreground">
                No video was recorded for this interview. The interviewee may have denied camera access.
              </p>
            </div>
          )}

          {/* Language & controls row */}
          {conversationTurns.length > 0 && (
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              {/* Original language label */}
              <div className="text-sm text-muted-foreground">
                Original Language: {originalLanguage || 'Unknown'}
              </div>

              {/* Controls */}
              <div className="flex flex-wrap items-center gap-2">
                {/* Translate button */}
                {canTranslate && onTranslate && (
                  <button aria-busy={translating}
                    onClick={onTranslate}
                    disabled={translating}
                    className="px-3 py-1.5 button-accent text-sm rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    <ButtonContent loading={translating}>
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129" />
                    </svg>
                    Translate
                    </ButtonContent>
                  </button>
                )}

                {/* Language toggle (when translation exists) */}
                {translatedTurns && onToggleTranslated && (
                  <div className="flex bg-surface rounded-lg p-1 border border-border">
                    <button
                      onClick={() => onToggleTranslated(false)}
                      className={`px-3 py-1.5 text-xs rounded-lg transition-all ${!showTranslated ? 'bg-surface-raised text-foreground' : 'text-muted-foreground hover:text-foreground'}`}
                    >
                      Original
                    </button>
                    <button
                      onClick={() => onToggleTranslated(true)}
                      className={`px-3 py-1.5 text-xs rounded-lg transition-all ${showTranslated ? 'bg-volt/20 text-volt-soft' : 'text-muted-foreground hover:text-foreground'}`}
                    >
                      {translatedLanguage || 'Translated'}
                    </button>
                  </div>
                )}

                {/* Copy button */}
                {onCopyTranscript && (
                  <button
                    onClick={onCopyTranscript}
                    className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-surface-hover transition-colors"
                    title="Copy transcript"
                  >
                    {copiedTranscript ? (
                      <IconClipboardCheck className="w-5 h-5 text-success" />
                    ) : (
                      <IconClipboard className="w-5 h-5" />
                    )}
                  </button>
                )}

                {/* Download button */}
                {onDownloadTranscript && (
                  <button
                    onClick={onDownloadTranscript}
                    className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-surface-hover transition-colors"
                    title="Download transcript"
                  >
                    <IconDownload className="w-5 h-5" />
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Translation error */}
          {translationError && (
            <div className="p-3 bg-wash-danger rounded-lg text-danger-soft text-sm">
              {translationError}
            </div>
          )}
        </div>

        <div
          ref={transcriptRef}
          className="flex-1 overflow-y-auto scrollbar-dark p-4 space-y-3 max-h-[500px] lg:max-h-[600px]"
        >
          {conversationTurns.length > 0 ? (
            displayTurns.map((turn, idx) => {
              const speakerLabel = turn.speaker === 'ai'
                ? 'Host'
                : turn.speaker === 'user'
                  ? 'Guest'
                  : 'System';

              const turnKey = displayTurnKeys[idx] ?? turnKeyForTurn(turn);
              const timing = getTurnTiming(timeline, turnKey);
              const seekTarget = seekTargetForTurn(timeline, turnKey);
              const isClickable = hasProcessedVideo && seekTarget !== null;
              const isActive = activeTurnKey !== null && activeTurnKey === turnKey;
              const turnDisplayTimeMs = getTurnDisplayTimeMs(turn, seekTarget);

              // Preserve original token offsets so annotations can wrap multiword
              // entities without changing the word indices used for playback.
              const wordMatches = Array.from(turn.text.matchAll(/\S+/g));
              const guestTurnIndex = conversationTurns.slice(0, idx + 1).filter(item => item.speaker === 'user').length;
              const turnInsights = turn.speaker === 'user' ? aiContext?.filter(insight => insight.turnIndex === guestTurnIndex) : [];

              return (
                <div
                  key={idx}
                  data-turn-index={idx}
                  data-active={isActive ? 'true' : undefined}
                  ref={(el) => { if (el) turnRefs.current.set(idx, el); }}
                  onClick={() => isClickable && handleTurnClick(turnKey)}
                  className={`p-3 rounded-lg transition-all group ${
                    turn.speaker === 'ai'
                      ? 'bg-wash-accent'
                      : turn.speaker === 'user'
                        ? 'bg-wash-success'
                        : 'bg-surface-raised/50 text-xs italic'
                  } ${
                    isActive ? 'ring-2 ring-volt' : ''
                  } ${
                    isClickable
                      ? 'cursor-pointer hover:ring-1 hover:ring-volt/50 active:scale-[0.99]'
                      : ''
                  }`}
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className={`text-xs font-bold uppercase tracking-wider ${
                      turn.speaker === 'ai'
                        ? 'text-volt-soft'
                        : turn.speaker === 'user'
                          ? 'text-success-soft'
                          : 'text-muted-foreground'
                    }`}>
                      {speakerLabel}
                    </span>
                    {isClickable ? (
                      <button
                        type="button"
                        aria-label={`Seek to ${speakerLabel} turn`}
                        onClick={(event) => {
                          event.stopPropagation();
                          handleTurnClick(turnKey);
                        }}
                        className="text-xs text-volt-soft flex items-center gap-1"
                      >
                        {turnDisplayTimeMs !== null && turnDisplayTimeMs >= 0
                          ? formatTime(turnDisplayTimeMs)
                          : null}
                        <svg className="w-3 h-3 opacity-60 group-hover:opacity-100 transition-opacity" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M8 5v14l11-7z" />
                        </svg>
                      </button>
                    ) : turnDisplayTimeMs !== null && turnDisplayTimeMs >= 0 ? (
                      <span className="text-xs flex items-center gap-1 text-muted-foreground">
                        {formatTime(turnDisplayTimeMs)}
                      </span>
                    ) : null}
                  </div>
                  <p className="text-sm text-foreground">
                    <InsightText text={turn.text} insights={turnInsights} renderText={(text, start, annotated) =>
                      Array.from(text.matchAll(/\s+|\S+/g)).map((part, partIndex) => {
                        const offset = start + part.index;
                        const wi = wordMatches.findIndex(word => offset >= word.index && offset < word.index + word[0].length);
                        if (wi < 0) return <span key={partIndex}>{part[0]}</span>;
                        const spoken = timing.hasWords && !isTranslatedView && wi < timing.spokenWordCount;
                        const seekable = spoken && !annotated;
                        const wordActive = isActive && activeWordIndex === wi;
                        return <span key={partIndex} data-word-index={wi} data-word-active={wordActive ? 'true' : undefined}
                          role={seekable ? 'button' : undefined} tabIndex={seekable ? 0 : undefined}
                          onClick={seekable ? event => { event.stopPropagation(); handleWordClick(turnKey, wi); } : undefined}
                          onKeyDown={seekable ? event => {
                            if (event.key === 'Enter' || event.key === ' ') {
                              event.preventDefault(); event.stopPropagation(); handleWordClick(turnKey, wi);
                            }
                          } : undefined}
                          className={`rounded transition-colors ${seekable ? 'cursor-pointer hover:text-volt-soft' : ''} ${timing.hasWords && !isTranslatedView && !spoken ? 'opacity-50' : ''} ${wordActive ? 'bg-volt/30' : ''}`}>
                          {part[0]}
                        </span>;
                      })
                    } />
                  </p>
                </div>
              );
            })
          ) : (
            <p className="text-muted-foreground text-center py-10">
              No transcript available yet.
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default VideoTranscriptViewer;
